<template>
  <div
    class="relative flex flex-col min-w-0 break-words bg-white rounded mb-6 xl:mb-0 shadow-lg"
    style="height: 145px; overflow: hidden;"
  >
    <div class="flex-auto p-6">
      <div class="flex flex-wrap">
        <div class="relative w-full  pt-2 max-w-full flex-grow flex-1">
          <h5 class="text-blueGray-400 uppercase font-bold text-sm">
            {{ statSubtitle }}
          </h5>
          <span
            class="font-semibold text-blueGray-700 inline-block"
            :class="titleAnimationClass"
            :style="{ fontSize: titleFontSize }"
          >
            {{ animatedTitle }}
          </span>
        </div>
        <div class="relative w-auto pl-2 flex-initial">
          <div
            class="text-white text-center inline-flex items-center justify-center w-14 h-14 shadow-lg rounded-full"
            :class="statIconBgColor ? '' : [statIconColor]"
            :style="statIconBgColor ? { backgroundColor: statIconBgColor } : {}"
          >
            <i class="text-xl" :class="[statIconName]"></i>
          </div>
        </div>
      </div>
      <p class="text-sm text-blueGray-400 mt-4">
        <span class="mr-2 inline-block transition-transform" :class="[statPercentColor, animationClass]">
          <i
            :class="[
              statArrow === 'up' ? `mdi mdi-arrow-up` : `mdi mdi-arrow-down`,
            ]"
          ></i>
          {{ animatedPercent }}
        </span>
      </p>
    </div>
  </div>
</template>
<script>
export default {
  name: "card-stats",
  props: {
    statSubtitle: {
      type: String,
      default: "Traffic",
    },
    statTitle: {
      type: String,
      default: "350,897",
    },
    statArrow: {
      default: "up",
      validator: function (value) {
        // The value must match one of these strings
        return ["up", "down"].indexOf(value) !== -1;
      },
    },
    statPercent: {
      type: String,
      default: "3.48",
    },
    // can be any of the text color utilities
    // from tailwindcss
    statPercentColor: {
      type: String,
      default: "text-emerald-500",
    },
    statDescripiron: {
      type: String,
      default: "Since last month",
    },
    statIconName: {
      type: String,
      default: "mdi mdi-chart-bar",
    },
    // can be any of the background color utilities
    // from tailwindcss
    statIconColor: {
      type: String,
      default: "bg-red-500",
    },
    // custom background color (hex code)
    statIconBgColor: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      animatedPercent: 0,
      animationClass: '',
      animatedTitle: '0',
      titleAnimationClass: ''
    };
  },
  computed: {
    titleFontSize() {
      const length = String(this.animatedTitle).length;
      // 글자 수에 따라 폰트 크기 조정
      if (length <= 6) {
        return '1.125rem'; // text-lg (18px)
      } else if (length <= 8) {
        return '1rem'; // text-base (16px)
      } else if (length <= 10) {
        return '0.875rem'; // text-sm (14px)
      } else if (length <= 12) {
        return '0.8rem'; // (12.8px)
      } else {
        return '0.75rem'; // text-xs (12px)
      }
    }
  },
  watch: {
    statPercent(newVal) {
      const targetValue = parseFloat(newVal) || 0;
      this.animateNumber(this.animatedPercent, targetValue);
    },
    statTitle(newVal) {
      const targetValue = this.parseNumberFromString(newVal);
      const currentValue = this.parseNumberFromString(this.animatedTitle);
      this.animateTitle(currentValue, targetValue);
    }
  },
  mounted() {
    // 초기 로딩 시 애니메이션
    const targetPercent = parseFloat(this.statPercent) || 0;
    this.animateNumber(0, targetPercent);

    const targetTitle = this.parseNumberFromString(this.statTitle);
    this.animateTitle(0, targetTitle);
  },
  methods: {
    parseNumberFromString(str) {
      // 쉼표 제거하고 숫자로 변환
      return parseFloat(String(str).replace(/,/g, '')) || 0;
    },
    animateNumber(start, end) {
      // 애니메이션 시작 시 펄스 효과
      this.animationClass = 'animate-pulse';

      const duration = 1200; // 1.2초
      const startTime = Date.now();
      const isInteger = !this.statPercent.includes('.');

      const animate = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // easeOutCubic 이징 함수 (부드러운 감속)
        const easeProgress = 1 - Math.pow(1 - progress, 3);

        const currentValue = start + (end - start) * easeProgress;

        // 정수 또는 소수점 형식 유지
        if (isInteger) {
          this.animatedPercent = Math.floor(currentValue);
        } else {
          this.animatedPercent = currentValue.toFixed(2);
        }

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // 애니메이션 종료 후 펄스 효과 제거
          this.animationClass = '';
          // 최종값 정확히 설정
          this.animatedPercent = isInteger ? Math.floor(end) : end.toFixed(2);
        }
      };

      requestAnimationFrame(animate);
    },
    animateTitle(start, end) {
      // 애니메이션 시작 시 펄스 효과
      this.titleAnimationClass = 'animate-pulse';

      const duration = 1500; // 1.5초 (큰 숫자라 조금 더 길게)
      const startTime = Date.now();

      const animate = () => {
        const currentTime = Date.now();
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // easeOutCubic 이징 함수 (부드러운 감속)
        const easeProgress = 1 - Math.pow(1 - progress, 3);

        const currentValue = start + (end - start) * easeProgress;

        // 숫자를 쉼표 포맷으로 변환
        this.animatedTitle = Math.floor(currentValue).toLocaleString();

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          // 애니메이션 종료 후 펄스 효과 제거
          this.titleAnimationClass = '';
          // 최종값 정확히 설정
          this.animatedTitle = Math.floor(end).toLocaleString();
        }
      };

      requestAnimationFrame(animate);
    }
  }
};
</script>

<style scoped>
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.animate-pulse {
  animation: pulse 0.6s ease-in-out;
}
</style>
